import { type Breakpoint } from '@divi/types';

import {
  type MultiViewDataItemStyle,
  type MultiViewDataUpdateStyle,
  type MultiViewUpdateElementParams,
} from '../../types';
import { addEventListenerHover } from '../add-event-listener-hover';
import { dispatchCustomEvent } from '../dispatch-custom-event';
import { parseData } from '../parse-data';

/**
 * Sets the inline style of an element and dispatches a custom event.
 *
 * @since ??
 *
 * @param {object} params The parameters needed to set the inline style of an element.
 * @param {MultiViewDataItemStyle} params.stateData The data to set as the inline style.
 * @param {MultiViewDataUpdateStyle} params.item The data item being updated.
 * @param {HTMLElement} params.element The element to update.
 * @param {Breakpoint.Name} params.breakpoint The current breakpoint.
 * @param {string} params.state The state of the element ('default' or 'hover').
 * @param {MouseEvent | TouchEvent} params.originalEvent The original event that triggered the update.
 */
const setElementInlineStyle = ({
  stateData,
  item,
  element,
  breakpoint,
  state,
  originalEvent,
}: MultiViewUpdateElementParams<MultiViewDataItemStyle>) => {
  const updatedData: MultiViewDataItemStyle = {};
  let updated = false;

  Object.keys(stateData).forEach(key => {
    const currentStyleValue = element.style.getPropertyValue(key) ?? '';
    const updateStyleValue = stateData[key];

    if (currentStyleValue !== updateStyleValue) {
      element.style.setProperty(key, updateStyleValue);
      updated = true;
      updatedData[key] = updateStyleValue;
    }
  });

  if (updated) {
    // Dispatch custom event: divi:multiView:setStyle
    dispatchCustomEvent<MultiViewDataItemStyle>({
      breakpoint,
      item,
      originalEvent,
      state,
      updatedData,
    });
  }
};

/**
 * Update element inline styles.
 *
 * @since ??
 *
 * @param {params} params The parameters needed to update the inline styles of an element.
 * @param {MultiViewDataUpdateStyle} params.item The item that will be updated.
 * @param {Breakpoint.Name} params.breakpoint The current breakpoint, which can be 'phone', 'tablet', or 'desktop'.
 * @param {boolean} params.isInit Whether this is the first time the function is being called.
 * @param {Breakpoint.Name} params.baseBreakpoint The base breakpoint.
 * @param {Breakpoint.Names} params.breakpointNames The names of the breakpoints.
 */
export const updateDataStyle = ({
  item,
  breakpoint,
  isInit = false,
  baseBreakpoint = 'desktop',
  breakpointNames = ['desktop', 'tablet', 'phone'],
}: {
  item: MultiViewDataUpdateStyle;
  breakpoint: Breakpoint.Name;
  isInit: boolean;
  baseBreakpoint: Breakpoint.Name;
  breakpointNames: Breakpoint.Names;
}): void => {
  const { element, hoverElements, data } = item;
  const { defaultStateData, hoverStateData } = parseData<MultiViewDataItemStyle>({
    data,
    breakpoint,
    baseBreakpoint,
    breakpointNames,
  });

  // Bail early default state data is null.
  // This can happen if the data is not valid.
  if (null === defaultStateData) {
    return;
  }

  const switchDataToDefaultState = (event?: MouseEvent | TouchEvent) => {
    setElementInlineStyle({
      stateData: defaultStateData,
      state: 'default',
      element,
      item,
      breakpoint,
      originalEvent: event,
    });
  };

  // Handle hover state.
  if (null !== hoverStateData) {
    const switchDataToHoverState = (event?: MouseEvent | TouchEvent) => {
      setElementInlineStyle({
        stateData: hoverStateData,
        state: 'hover',
        element,
        item,
        breakpoint,
        originalEvent: event,
      });
    };

    hoverElements.forEach(hoverElement => {
      addEventListenerHover(
        element,
        hoverElement,
        switchDataToDefaultState,
        switchDataToHoverState,
        'update-data-style',
      );
    });
  }

  // Switch to default state on page load or when the breakpoint changes that triggered when the user
  // resizing the browser or changing the device orientation.
  if ('desktop' === breakpoint && isInit && !item.switchOnLoad) {
    return;
  }

  switchDataToDefaultState();
};
